import { useState } from 'react';
import { Download, History } from 'lucide-react';
import {
  DataTable,
  DataTableBody,
  DataTableCard,
  DataTableCell,
  DataTableHead,
  DataTableHeadCell,
  DataTableRow,
} from '@/components/common/DataTable';
import { EmptyState } from '@/components/common/EmptyState';
import { PageShell } from '@/components/common/PageShell';
import { QueryErrorState } from '@/components/common/QueryErrorState';
import { TablePaginationBar } from '@/components/common/TablePaginationBar';
import { TableSkeleton } from '@/components/common/TableSkeleton';
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar';
import { Badge, Button } from '@/components/ui';
import { formatDate } from '@/lib/formatters';
import type { ImportHistoryItem } from '@/types/setup-wizard.types';
import { useDownloadImportSetAside, useImportHistory } from '../hooks/useSetupWizard';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

function personName(actor: ImportHistoryItem['actorId']): string {
  if (!actor) return '—';
  return [actor.firstName, actor.lastName].filter(Boolean).join(' ') || actor.email;
}

/** Only what actually happened: a zero tells the reader nothing. */
function resultSummary({ status, totals, failedReason }: ImportHistoryItem): string {
  if (status === 'failed') return failedReason || 'Nothing was imported';
  const parts = [
    totals.created ? `${totals.created} created` : null,
    totals.updated ? `${totals.updated} updated` : null,
    totals.linked ? `${totals.linked} linked` : null,
    totals.skipped ? `${totals.skipped} skipped` : null,
    totals.setAside ? `${totals.setAside} set aside` : null,
  ].filter(Boolean);
  return parts.join(' · ') || 'No changes';
}

export function ImportHistoryPage(): React.ReactElement {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE_OPTIONS[0]);
  const { data, isLoading, error, refetch } = useImportHistory(page, limit);
  const downloadSetAside = useDownloadImportSetAside();
  const total = data?.total ?? 0;
  const items = data?.items ?? [];

  return (
    <PageShell
      breadcrumbItems={[{ label: 'Companies', to: '/companies' }, { label: 'Import history' }]}
      icon={History}
      title="Import history"
      description="Spreadsheets imported into this organization."
      className="gap-0"
      hideHeader
    >
      <div className="space-y-4">
        <WorkspaceToolbar
          filter={
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {total} {total === 1 ? 'import' : 'imports'}
            </span>
          }
        />

        {error ? (
          <QueryErrorState error={error} title="Couldn't load import history" onRetry={refetch} />
        ) : isLoading ? (
          <DataTableCard>
            <TableSkeleton rows={5} cols={5} />
          </DataTableCard>
        ) : items.length === 0 ? (
          <DataTableCard>
            <EmptyState
              icon={History}
              title="No imports yet"
              description="Spreadsheets you import from the setup wizard will show up here."
            />
          </DataTableCard>
        ) : (
          <DataTableCard>
            <DataTable>
              <DataTableHead>
                <tr>
                  <DataTableHeadCell>File</DataTableHeadCell>
                  <DataTableHeadCell className="hidden md:table-cell">Company</DataTableHeadCell>
                  <DataTableHeadCell className="hidden md:table-cell">Imported by</DataTableHeadCell>
                  <DataTableHeadCell>Result</DataTableHeadCell>
                  <DataTableHeadCell className="hidden sm:table-cell">Date</DataTableHeadCell>
                </tr>
              </DataTableHead>
              <DataTableBody>
                {items.map((item) => (
                  <DataTableRow key={item._id}>
                    <DataTableCell className="max-w-32 truncate font-medium text-gray-900 sm:max-w-56 dark:text-gray-100">
                      <span title={item.originalFilename}>{item.originalFilename}</span>
                    </DataTableCell>
                    <DataTableCell className="hidden text-gray-600 md:table-cell dark:text-gray-400">
                      {item.companyId?.name ?? '—'}
                    </DataTableCell>
                    <DataTableCell className="hidden text-gray-600 md:table-cell dark:text-gray-400">
                      {personName(item.actorId)}
                    </DataTableCell>
                    <DataTableCell>
                      <span className="flex min-w-0 flex-col items-start gap-1 lg:flex-row lg:items-center lg:gap-2">
                        <Badge variant={item.status === 'completed' ? 'success' : 'danger'}>
                          {item.status === 'completed' ? 'Completed' : 'Failed'}
                        </Badge>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {resultSummary(item)}
                        </span>
                        {item.totals.setAside ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2 text-xs"
                            onClick={() =>
                              downloadSetAside.mutate({
                                importId: item._id,
                                filename: item.originalFilename,
                              })
                            }
                            loading={
                              downloadSetAside.isPending &&
                              downloadSetAside.variables?.importId === item._id
                            }
                            aria-label={`Download the ${item.totals.setAside} set-aside rows of ${item.originalFilename}`}
                          >
                            <Download className="h-3.5 w-3.5" aria-hidden />
                            Rows to fix
                          </Button>
                        ) : null}
                      </span>
                    </DataTableCell>
                    <DataTableCell className="hidden whitespace-nowrap text-gray-600 sm:table-cell dark:text-gray-400">
                      {item.createdAt ? formatDate(item.createdAt, 'PP') : '—'}
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </DataTableBody>
            </DataTable>
            <div className="border-t border-gray-200 px-4 py-3 dark:border-gray-700">
              <TablePaginationBar
                page={page}
                limit={limit}
                total={total}
                totalPages={Math.max(1, Math.ceil(total / limit))}
                onPageChange={setPage}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onLimitChange={(next) => {
                  setLimit(next);
                  setPage(1);
                }}
              />
            </div>
          </DataTableCard>
        )}
      </div>
    </PageShell>
  );
}

ImportHistoryPage.displayName = 'ImportHistoryPage';
